import { useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ChevronDown, Globe, Shield, Cpu, Zap, Users, Building2, Layers, BookOpen, TrendingUp, Lock, GraduationCap, Rocket, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/images";
import PriorityListModal from "../components/shared/PriorityListModal";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-70px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}>
      {children}
    </motion.div>
  );
}

/* ── Hero ─────────────────────────────────────────── */
function HeroSection({ onPriority }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const lines = ["Canada's", "Leading", "AI Summit", "+ Tradeshow."];

  return (
    <section ref={ref} className="relative overflow-hidden" style={{ minHeight: "100svh", display: "flex", alignItems: "center" }} data-testid="hero-section">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img src={IMAGES.niagara} alt="Niagara Falls — Core AI Summit + Tradeshow 2027" className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(9, 13, 22, 0.78) 0%, rgba(9, 13, 22, 0.35) 30%, rgba(9, 13, 22, 0.45) 62%, rgba(9, 13, 22, 0.70) 80%, rgb(9, 13, 22) 100%)" }} />
      </motion.div>

      <motion.div className="relative z-10 container-xl w-full" style={{ opacity, paddingTop: "110px", paddingBottom: "40px" }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center gap-3 mb-5">
          <span className="w-8 h-px" style={{ background: "var(--cyan)" }} />
          <span className="overline-label">June 2027 · Niagara Falls, Ontario</span>
        </motion.div>

        <div className="mb-4">
          {lines.map((line, i) => (
            <div key={i} className="overflow-hidden">
              <motion.h1 initial={{ y: "110%", skewY: 3 }} animate={{ y: "0%", skewY: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: [0.76, 0, 0.24, 1] }}
                className="block font-black leading-[1] text-white"
                style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(40px, 9vw, 112px)" }}>
                {i === 3 ? <>+ <span style={{ color: "var(--cyan)" }}>Tradeshow.</span></> : line}
              </motion.h1>
            </div>
          ))}
        </div>

        {/* Format strip — makes trade show prominent immediately */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.6 }}
          className="mb-6">
          <span className="text-xs tracking-[0.28em] uppercase" style={{ color: "rgba(0,212,255,0.8)", fontFamily: "var(--font-mono)" }}>
            Conference&nbsp;&nbsp;•&nbsp;&nbsp;Trade Show&nbsp;&nbsp;•&nbsp;&nbsp;Workshops&nbsp;&nbsp;•&nbsp;&nbsp;Demonstrations&nbsp;&nbsp;•&nbsp;&nbsp;Networking
          </span>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
          className="max-w-2xl mb-10">
          <p className="text-xl font-bold text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            Canada's AI Future Starts Here.
          </p>
          <p className="text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Core AI Summit brings together the people building artificial intelligence with the businesses, governments and organizations preparing to adopt it. Explore what AI can do today. Understand what's coming next. Build your path forward.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.3 }}
          className="flex flex-wrap gap-4 mb-16">
          <button onClick={onPriority} className="btn-primary" data-testid="hero-priority-btn">
            Join Priority List <ArrowRight size={14} />
          </button>
          <Link to="/partner" className="btn-outline" data-testid="hero-partner-btn">
            Partner With Core AI Summit
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.5 }}
          className="py-8 mt-8"
          style={{
            background: "linear-gradient(90deg, #0057A8 0%, #0099CC 100%)",
            width: "100vw",
            marginLeft: "calc(50% - 50vw)",
          }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto px-8">
          {[{ v: "100+", l: "Exhibitors" }, { v: "30+", l: "Startups" }, { v: "80+", l: "Speakers" }, { v: "60+", l: "Sessions & Workshops" }].map(({ v, l }) => (
            <div key={l} className="text-center" data-testid={`stat-${l.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="font-black text-white mb-1" style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(28px,4vw,48px)" }}>{v}</div>
              <div className="text-xs uppercase tracking-widest text-white" style={{ opacity: 0.85, fontFamily: "var(--font-mono)" }}>{l}</div>
            </div>
          ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
        <ChevronDown size={20} style={{ color: "rgba(0,212,255,0.4)" }} />
      </motion.div>
    </section>
  );
}

/* ── Topic Marquee ─────────────────────────────────── */
const MARQUEE_TOPICS = [
  "AI Adoption & Enterprise Transformation", "The Next 5 Years of AI",
  "Canadian AI & Sovereign Technology", "AI Infrastructure, Compute & Energy",
  "Physical AI, Robotics & Autonomous Systems", "Responsible AI, Security & Trust",
  "Workforce, Education & AI Literacy", "Startups, Investment & Commercialization",
];

function TopicMarquee() {
  return (
    <div className="py-5 overflow-hidden" style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <Marquee speed={35} gradient={false} pauseOnHover>
        {MARQUEE_TOPICS.map((t) => (
          <div key={t} className="marquee-item">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: "var(--cyan)" }} />
            {t}
          </div>
        ))}
      </Marquee>
    </div>
  );
}

/* ── Why We Exist ──────────────────────────────────── */
function WhyWeExistSection() {
  const questions = [
    "Which technologies matter?",
    "What should we invest in?",
    "What will AI look like three to five years from now?",
    "How do we prepare our people and infrastructure?",
    "How do we turn experimentation into a practical AI strategy?",
  ];
  return (
    <section className="section-py" style={{ backgroundColor: "#EFF4FA" }} data-testid="why-exist-section">
      <div className="container-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <RevealSection>
            <div className="overline-label mb-4" style={{ color: "#0078A8" }}>Why Core AI Summit Exists</div>
            <h2 className="text-4xl sm:text-5xl font-black mb-4 leading-tight"
              style={{ fontFamily: "var(--font-heading)", color: "#0D1117" }}>
              AI is moving fast.<br />Most organizations are still finding their way.
            </h2>
          </RevealSection>
          <RevealSection delay={0.12}>
            <div className="space-y-5 text-base leading-relaxed" style={{ color: "#4B5563" }}>
              <p>Artificial intelligence is transforming how organizations operate, compete and grow. But for many business leaders, the biggest challenge isn't understanding that AI matters — it's knowing where to begin.</p>
              <div className="space-y-2.5">
                {questions.map((q) => (
                  <div key={q} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "#0078A8" }} />
                    <span>{q}</span>
                  </div>
                ))}
              </div>
              <p className="font-medium" style={{ color: "#0D1117" }}>Core AI Summit + Tradeshow is being built to help answer those questions.</p>
              <p>We bring together researchers, technology companies, business leaders, government, investors and innovators to connect what is being developed with the organizations that need to understand and deploy it.</p>
            </div>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── What's Next ───────────────────────────────────── */
const FUTURE_TOPICS = [
  "AI adoption and enterprise transformation",
  "Generative AI and next-generation models",
  "AI agents and autonomous systems",
  "Physical AI and robotics",
  "Canadian AI and sovereign technology",
  "Compute, cloud and AI infrastructure",
  "Energy and data centres",
  "Responsible and trustworthy AI",
  "Cybersecurity and data",
  "Workforce transformation and AI literacy",
  "Startups, commercialization and investment",
  "International AI collaboration",
];

function WhatsNextSection({ onPriority }) {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="whats-next-section">
      <div className="container-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <RevealSection>
            <div className="overline-label mb-4">Don't Just Learn Where AI Is</div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "var(--font-heading)" }}>
              Understand where it's going.
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
              AI is evolving too quickly for organizations to plan around today's technology alone. Core AI Summit + Tradeshow will look beyond today's tools and applications to explore what artificial intelligence could mean over the next three to five years — and what organizations should be doing now to prepare.
            </p>
            <button onClick={onPriority} className="btn-primary">
              Join Priority List <ArrowRight size={14} />
            </button>
          </RevealSection>
          <RevealSection delay={0.12}>
            <div className="overline-label mb-5">Programming Will Explore</div>
            <div className="space-y-3">
              {FUTURE_TOPICS.map((t, i) => (
                <motion.div key={t} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                  className="flex items-start gap-3 text-sm" style={{ color: "var(--text-muted)" }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "var(--cyan)" }} />
                  {t}
                </motion.div>
              ))}
            </div>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── Programming Pillars ───────────────────────────── */
const PILLARS = [
  { num: "01", icon: Building2, title: "AI Adoption & Enterprise Transformation", color: "#00D4FF" },
  { num: "02", icon: TrendingUp, title: "The Next 5 Years of AI", color: "#7B2FBE" },
  { num: "03", icon: Globe, title: "Canadian AI & Sovereign Technology", color: "#00D4FF" },
  { num: "04", icon: Cpu, title: "AI Infrastructure, Compute & Energy", color: "#7B2FBE" },
  { num: "05", icon: Zap, title: "Physical AI, Robotics & Autonomous Systems", color: "#00D4FF" },
  { num: "06", icon: Shield, title: "Responsible AI, Security & Trust", color: "#7B2FBE" },
  { num: "07", icon: GraduationCap, title: "Workforce, Education & AI Literacy", color: "#00D4FF" },
  { num: "08", icon: Rocket, title: "Startups, Investment & Commercialization", color: "#7B2FBE" },
];

function ProgrammingPillarsSection() {
  return (
    <section className="section-py" style={{ backgroundColor: "#EFF4FA" }} data-testid="pillars-section">
      <div className="container-xl">
        <RevealSection>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline-label mb-4" style={{ color: "#0078A8" }}>Programming Pillars</div>
              <h2 className="text-4xl font-black" style={{ fontFamily: "var(--font-heading)", color: "#0D1117" }}>
                Core AI will cover a wide range of AI topics including:
              </h2>
            </div>
            <Link to="/program" className="btn-ghost flex-shrink-0" style={{ color: "#374151" }}>Full Program <ArrowRight size={12} /></Link>
          </div>
        </RevealSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <RevealSection key={p.num} delay={i * 0.05}>
                <div className="p-5 h-full group rounded-xl flex flex-col items-center text-center"
                  style={{
                    background: "linear-gradient(135deg, #E8F4FF 0%, #D6ECFA 100%)",
                    border: "1px solid #B8D8F0",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0078A8"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,120,168,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#B8D8F0"; e.currentTarget.style.boxShadow = "none"; }}
                  data-testid={`pillar-${p.num}`}>
                  <div className="flex items-center justify-center mb-4">
                    <Icon size={44} strokeWidth={1.25} style={{ color: "#0078A8" }} />
                  </div>
                  <h4 className="text-sm font-bold leading-snug" style={{ fontFamily: "var(--font-heading)", color: "#0D1117" }}>{p.title}</h4>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Canadian AI Leadership ────────────────────────── */
function CanadianLeadershipSection() {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="canada-leadership-section">
      <div className="container-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <RevealSection>
            <div>
              <div className="overline-label mb-4">Canadian AI Leadership</div>
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                Built in Canada.<br />Connected to the world.
              </h2>
              <div className="space-y-5 text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                <p>Canada helped pioneer modern artificial intelligence. Core AI Summit + Tradeshow is being created to connect Canada's extraordinary research and technology ecosystem with the businesses, governments, investors and international partners that can help turn Canadian innovation into economic opportunity.</p>
                <p>The Summit will create a meeting place for Canadian AI companies, researchers and institutions while building connections with technology ecosystems around the world.</p>
              </div>
              <div className="mt-8">
                <Link to="/about" className="btn-primary">About Core AI Summit <ArrowRight size={14} /></Link>
              </div>
            </div>
          </RevealSection>
          <RevealSection delay={0.15}>
            <div className="relative rounded-2xl overflow-hidden">
              <img src={IMAGES.networking} alt="Canadian AI community" className="w-full h-80 object-cover opacity-80" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(9,13,22,0.7) 0%, transparent 60%)" }} />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="overline-label mb-2">The Vision</div>
                <p className="text-sm font-medium text-white">Connecting Canadian innovation with global markets and international collaboration.</p>
              </div>
            </div>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── AI Sovereignty / Canadian Capability ──────────── */
const CIRCUIT_BG = "https://static.prod-images.emergentagent.com/jobs/6bb71053-4e67-4930-bf10-a7af9cda97ea/images/a6f1a81e4318e7a2a1587c6b2d91b1586c520352dcf49a738585e02b1579b4d0.jpeg";

function CanadianCapabilitySection() {
  return (
    <section className="section-py relative overflow-hidden" data-testid="sovereignty-section"
      style={{ backgroundColor: "#EFF4FA" }}>
      {/* Faint circuit background */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${CIRCUIT_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.18,
        }} />
      <div className="container-xl relative z-10">
        <div className="max-w-3xl">
          <RevealSection>
            <div className="overline-label mb-4" style={{ color: "#0078A8" }}>Building Canada's AI Capability</div>
            <h2 className="text-4xl sm:text-5xl font-black mb-6 leading-tight"
              style={{ fontFamily: "var(--font-heading)", color: "#0D1117" }}>
              Where our technology is developed matters.
            </h2>
            <div className="space-y-5 text-base leading-relaxed" style={{ color: "#4B5563" }}>
              <p>The next chapter of AI will raise important questions about where our technology is developed, where our data is stored, where computing infrastructure is located and who controls the systems increasingly powering our economy.</p>
              <p>Core AI Summit + Tradeshow will bring together technology, business and government leaders to explore Canada's role in building its own AI capabilities while remaining connected to global markets and international collaboration.</p>
              <p className="font-medium" style={{ color: "#0D1117" }}>This is a constructive and forward-looking conversation — about building Canadian capability, not about limiting international partnership.</p>
            </div>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── Trade Show Section ────────────────────────────── */
const TRADESHOW_CATEGORIES = [
  "AI Platforms & Models", "Enterprise AI Solutions", "Cloud & Compute Infrastructure",
  "Chips & Hardware", "Data & Analytics Platforms", "Robotics & Physical AI",
  "Autonomous Systems", "Cybersecurity", "Data Centre Technology",
  "Energy & Power Solutions", "AI Consulting & Implementation", "Education & Workforce Solutions",
];

function TradeShowSection({ onExhibit }) {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="tradeshow-section">
      <div className="container-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <RevealSection>
            <div>
              <div className="overline-label mb-4">The Trade Show</div>
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                The technology behind the conversation.
              </h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
                Core AI Summit + Tradeshow will bring AI out of the presentation room and onto the show floor.
              </p>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
                Attendees should be able to hear about a technology on stage, see it demonstrated on the trade show floor, meet the company behind it and understand how it could apply to their organization.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/expo" className="btn-primary">Exhibit at Core AI Summit <ArrowRight size={14} /></Link>
                <Link to="/expo" className="btn-outline">View Expo Details</Link>
              </div>
            </div>
          </RevealSection>
          <RevealSection delay={0.12}>
            <div>
              <div className="overline-label mb-5">Meet the companies building and deploying</div>
              <div className="grid grid-cols-2 gap-2.5">
                {TRADESHOW_CATEGORIES.map((cat, i) => (
                  <motion.div key={cat} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                    style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: "var(--cyan)" }} />
                    {cat}
                  </motion.div>
                ))}
              </div>
            </div>
          </RevealSection>
        </div>

        {/* Expo image */}
        <RevealSection delay={0.2} className="mt-16">
          <div className="relative rounded-2xl overflow-hidden">
            <img src={IMAGES.expo} alt="Core AI Summit + Tradeshow exhibition floor" className="w-full h-64 object-cover opacity-85" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(9,13,22,0.8) 0%, transparent 60%)" }} />
            <div className="absolute inset-0 flex items-center">
              <div className="container-xl">
                <div className="overline-label mb-2">Core AI Summit + Tradeshow 2027</div>
                <p className="text-lg font-bold text-white max-w-md" style={{ fontFamily: "var(--font-heading)" }}>
                  60+ exhibitors · 3 demonstration stages · Startup pavilion
                </p>
              </div>
            </div>
          </div>
        </RevealSection>
      </div>
    </section>
  );
}

/* ── Practical AI Adoption ─────────────────────────── */
function PracticalAdoptionSection({ onPriority }) {
  return (
    <section className="section-py" data-testid="practical-adoption-section">
      <div className="container-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <RevealSection delay={0.1}>
            <img src={IMAGES.workshop} alt="Hands-on AI workshops" className="rounded-2xl w-full h-72 object-cover opacity-85" />
          </RevealSection>
          <RevealSection>
            <div>
              <div className="overline-label mb-4">Practical AI Adoption</div>
              <h2 className="text-4xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                From "we need AI" to "here's our plan."
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: "var(--text-muted)" }}>
                For organizations that know AI matters but aren't sure where to begin, Core AI Summit + Tradeshow will provide practical education designed to turn uncertainty into action.
              </p>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
                Through workshops, case studies, demonstrations and conversations with technology providers, attendees will be able to explore where AI fits within their organizations and begin developing a realistic path toward adoption.
              </p>
              <button onClick={onPriority} className="btn-primary">
                Register Interest <ArrowRight size={14} />
              </button>
            </div>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── Who Should Attend ─────────────────────────────── */
const AUDIENCE_GROUPS = [
  { icon: Building2, title: "Business Leaders", desc: "Understand how AI will affect your organization and how to prepare." },
  { icon: Cpu, title: "AI & Technology Leaders", desc: "Explore emerging technologies, infrastructure and applications." },
  { icon: BookOpen, title: "Developers & Researchers", desc: "Connect research and innovation with real-world adoption." },
  { icon: Globe, title: "Government & Policy Leaders", desc: "Explore infrastructure, sovereignty, responsible AI and economic development." },
  { icon: Rocket, title: "Startups & Entrepreneurs", desc: "Meet investors, enterprise customers and strategic partners." },
  { icon: TrendingUp, title: "Investors", desc: "Discover Canadian and international AI companies and emerging technologies." },
  { icon: Layers, title: "IT & Data Leaders", desc: "Evaluate platforms, infrastructure, security and implementation strategies." },
  { icon: GraduationCap, title: "Workforce & Education Leaders", desc: "Prepare people and organizations for an AI-driven economy." },
];

function WhoShouldAttendSection({ onPriority }) {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="who-attends-section">
      <div className="container-xl">
        <RevealSection>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline-label mb-4">Who Should Attend</div>
              <h2 className="text-4xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
                Built for every leader navigating AI.
              </h2>
            </div>
            <button onClick={onPriority} className="btn-primary flex-shrink-0">
              Join Priority List <ArrowRight size={13} />
            </button>
          </div>
        </RevealSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AUDIENCE_GROUPS.map((g, i) => {
            const Icon = g.icon;
            return (
              <RevealSection key={g.title} delay={i * 0.06}>
                <div className="card card-glow p-6 h-full" data-testid={`audience-${g.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                    <Icon size={16} style={{ color: "var(--cyan)" }} />
                  </div>
                  <h4 className="font-black text-white mb-2 text-sm" style={{ fontFamily: "var(--font-heading)" }}>{g.title}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{g.desc}</p>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Why Niagara ───────────────────────────────────── */
function WhyNiagaraSection() {
  return (
    <section data-testid="niagara-section">
      {/* Full-bleed image */}
      <div className="relative h-[60vh] min-h-[440px] overflow-hidden">
        <img src={IMAGES.niagara} alt="Niagara Falls at night" className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(9,13,22,0.2) 0%, rgba(9,13,22,0.85) 100%)" }} />
        <div className="absolute bottom-0 left-0 right-0 container-xl pb-12">
          <RevealSection>
            <div className="overline-label mb-3">Why Niagara Falls?</div>
            <h2 className="text-4xl sm:text-5xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
              An international destination at the intersection of technology, commerce and power.
            </h2>
          </RevealSection>
        </div>
      </div>

      {/* Why Niagara copy */}
      <div className="section-py">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <RevealSection>
              <div className="space-y-5 text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                <p>Niagara Falls is one of Canada's most recognized international destinations and sits at the gateway between Canada's largest economic region and the northeastern United States.</p>
                <p>Approximately 130 million people live within a one-day drive of Niagara, creating extraordinary access to Canadian and U.S. companies, investors, technology leaders and attendees.</p>
              </div>
            </RevealSection>
            <RevealSection delay={0.12}>
              <div>
                <div className="overline-label mb-4">Powered by Innovation for More Than a Century</div>
                <p className="text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  Niagara Falls has been synonymous with hydroelectric power, electrification and technological progress for generations. As artificial intelligence creates unprecedented demand for computing infrastructure, Niagara provides a fitting place to bring together the people shaping the next era of technology.
                </p>
                <div className="mt-8">
                  <Link to="/plan" className="btn-outline">Plan Your Visit <ArrowRight size={13} /></Link>
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── International Positioning ─────────────────────── */
function InternationalSection() {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="international-section">
      <div className="container-xl">
        <div className="max-w-3xl mx-auto text-center">
          <RevealSection>
            <div className="overline-label mb-4">Canadian at its Core. International in its Reach.</div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "var(--font-heading)" }}>
              Minutes from the Canada–U.S. border. Open to the world.
            </h2>
            <p className="text-base leading-relaxed mb-5" style={{ color: "var(--text-muted)" }}>
              Located at the Canada–U.S. border, Core AI Summit + Tradeshow will create a natural meeting place for Canadian and American technology ecosystems while welcoming researchers, companies, investors and government delegations from around the world.
            </p>
            <p className="text-base leading-relaxed mb-5" style={{ color: "var(--text-muted)" }}>
              Over 130 million people live within a one-day drive of the Niagara region — providing access to major Canadian centres and the northeastern and midwestern United States.
            </p>
            <p className="text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
              The goal is to showcase Canadian AI leadership while creating new opportunities for investment, partnerships, commercialization and international collaboration.
            </p>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

/* ── Founder Section removed from homepage — see /about page ── */
function FounderSection() { return null; }

/* ── Partner Strip ─────────────────────────────────── */
function PartnerStrip({ onPriority }) {
  return (
    <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="partner-strip">
      <div className="container-xl">
        <RevealSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="overline-label mb-4">Partner with Core AI Summit</div>
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                Get in front of the organizations shaping Canada's AI future.
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
                Sponsoring Core AI Summit + Tradeshow places your organization in front of the business leaders, government officials, researchers and technology decision-makers who are actively working to understand and deploy AI.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/partner" className="btn-primary">Request Partnership Details <ArrowRight size={14} /></Link>
                <button onClick={onPriority} className="btn-outline">Register as Attendee</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { tier: "Title Sponsor", desc: "Maximum brand presence & event naming rights", slots: "1 Exclusive" },
                { tier: "Platinum", desc: "Mainstage visibility and speaking access", slots: "2 Available" },
                { tier: "Gold", desc: "Speaking opportunity and exhibition space", slots: "5 Available" },
                { tier: "Silver", desc: "Brand presence and exhibitor access", slots: "10 Available" },
              ].map((t) => (
                <div key={t.tier} className="p-5 rounded-xl" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
                  data-testid={`sponsor-tier-${t.tier.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{t.tier}</div>
                  <div className="text-sm font-medium text-white mb-1">{t.desc}</div>
                  <div className="text-xs" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>{t.slots}</div>
                </div>
              ))}
            </div>
          </div>
        </RevealSection>
      </div>
    </section>
  );
}

/* ── Main Export ─────────────────────────────────────── */
export default function HomePage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div data-testid="home-page">
      <HeroSection onPriority={() => setShowModal(true)} />
      <TopicMarquee />
      <WhyWeExistSection />
      <WhatsNextSection onPriority={() => setShowModal(true)} />
      <ProgrammingPillarsSection />
      <CanadianLeadershipSection />
      <CanadianCapabilitySection />
      <TradeShowSection onExhibit={() => {}} />
      <PracticalAdoptionSection onPriority={() => setShowModal(true)} />
      <WhoShouldAttendSection onPriority={() => setShowModal(true)} />
      <WhyNiagaraSection />
      <InternationalSection />
      <FounderSection />
      <PartnerStrip onPriority={() => setShowModal(true)} />
      <CTABand
        title="Be Part of Core AI Summit + Tradeshow 2027"
        subtitle="Canada's conference and trade show for AI adoption, infrastructure, innovation and what's coming next."
        primaryLabel="Join Priority List"
        secondaryLabel="Partner With Us"
        secondaryHref="/partner"
      />
      <PriorityListModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}
